/**
 * DashboardPage — Unified portfolio + activity overview.
 *
 * Rebuilt to consume from the userActivity aggregator (single source of truth)
 * instead of the old bot-centric data flow. Every metric here is computed
 * from REAL trades the user has executed: CEX Trading, Smart Trade, Algo
 * Orders, DEX Swaps, DEX Limit Orders, and Futures.
 *
 * Sections (top to bottom):
 *   1. Hero metrics — total balance, 24h activity, 7d/30d activity
 *   2. 30-day trade volume area chart
 *   3. Source breakdown (CEX/DEX/Smart Trade/Algo split)
 *   4. Top trading pairs
 *   5. Top exchanges
 *   6. Recent unified activity feed
 *   7. Onboarding wizard resume (preserved from old version)
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, TrendingUp, TrendingDown, DollarSign, Euro, PoundSterling, IndianRupee, JapaneseYen, CircleDollarSign, Clock, ExternalLink,
  ArrowRight, Sparkles, Zap, Globe, Target, BarChart3, Layers,
  ArrowUpRight, ArrowDownRight, Wallet,
} from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useUserActivity } from '../hooks/useData';
import { Card, Spinner, EmptyState, InfoTip } from '../components/ui';
import OnboardingWizard, { useOnboardingTrigger } from '../components/OnboardingWizard';
import { formatUsd, getExplorerUrl, shortenTxHash } from '../services/api';
import { formatUsdCompact } from '../services/displayPrefs';
import { useCurrency } from '../hooks/useCurrency';

// Pick the lucide icon that matches the user's selected display currency. Used
// in HeroMetric cards so the icon in the top-right reads as "this number is in
// euros / yen / pounds / etc" rather than always showing a dollar sign even
// when the value below is converted to another currency.
function getCurrencyIcon(currency) {
  switch (currency) {
    case 'EUR': return Euro;
    case 'GBP': return PoundSterling;
    case 'INR': return IndianRupee;
    case 'JPY':
    case 'CNY': return JapaneseYen; // Yuan + Yen share the ¥ symbol
    case 'USD':
    case 'AUD':
    case 'CAD':
    case 'SGD':
    case 'HKD':
    case 'MXN': return DollarSign;
    default: return CircleDollarSign; // generic fallback for CHF, TRY, ZAR, KRW, BRL, etc.
  }
}
import { format, formatDistanceToNow } from 'date-fns';

// ─── Visual constants ──────────────────────────────────────────────────────

const SOURCE_COLORS = {
  cex_manual:   { bg: 'bg-amber-500/15',    text: 'text-amber-400',    hex: '#f59e0b', label: 'CEX' },
  cex_futures:  { bg: 'bg-red-500/15',      text: 'text-red-400',      hex: '#ef4444', label: 'Futures' },
  dex_swap:     { bg: 'bg-blue-500/15',     text: 'text-blue-400',     hex: '#3b82f6', label: 'DEX Swap' },
  dex_limit:    { bg: 'bg-cyan-500/15',     text: 'text-cyan-400',     hex: '#06b6d4', label: 'DEX Limit' },
  smart_trade:  { bg: 'bg-emerald-500/15',  text: 'text-emerald-400',  hex: '#10b981', label: 'Smart Trade' },
  algo_twap:    { bg: 'bg-violet-500/15',   text: 'text-violet-400',   hex: '#8b5cf6', label: 'TWAP' },
  algo_vwap:    { bg: 'bg-violet-500/15',   text: 'text-violet-400',   hex: '#a78bfa', label: 'VWAP' },
  algo_iceberg: { bg: 'bg-violet-500/15',   text: 'text-violet-400',   hex: '#c4b5fd', label: 'Iceberg' },
  algo_bracket: { bg: 'bg-violet-500/15',   text: 'text-violet-400',   hex: '#ddd6fe', label: 'Bracket' },
};

function sourceStyle(source) {
  return SOURCE_COLORS[source] || { bg: 'bg-gray-500/15', text: 'text-gray-400', hex: '#6b7280', label: source };
}

// ─── Reusable cards ────────────────────────────────────────────────────────

function HeroMetric({ label, value, subValue, accent = 'amber', icon: Icon, trend, info }) {
  const colorMap = {
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    violet: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
  };
  return (
    <div className={`rounded-2xl p-5 border ${colorMap[accent]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5">
          {label}
          {info && <InfoTip side="bottom">{info}</InfoTip>}
        </span>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <p className="text-2xl font-bold font-mono">{value}</p>
      {subValue && <p className="text-[11px] text-gray-500 mt-1">{subValue}</p>}
      {trend !== undefined && trend !== null && (
        <p className={`text-[10px] mt-1 flex items-center gap-1 ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
        </p>
      )}
    </div>
  );
}

function SectionCard({ title, icon: Icon, action, children, className = '' }) {
  return (
    <div className={`bg-dark-800 rounded-2xl border border-dark-700/50 p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-amber-400" />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { id: 7,   label: '7d' },
  { id: 30,  label: '30d' },
  { id: 60,  label: '60d' },
  { id: 90,  label: '90d' },
  { id: 'all', label: 'All' },
];

export default function DashboardPage() {
  const onboarding = useOnboardingTrigger();
  const [showWizardManually, setShowWizardManually] = useState(false);
  const [range, setRange] = useState(30); // default 30 days
  // Subscribe to currency context so the page RE-RENDERS the moment the user
  // picks a different display currency. Without this hook, the cards still
  // showed the old converted value for ~10 seconds (until something else
  // triggered a re-render) — formatUsd reads from a singleton, but React
  // doesn't know the singleton changed.
  const { currency } = useCurrency();
  const CurrencyIcon = getCurrencyIcon(currency);
  const { summary, loading, error, refresh } = useUserActivity({
    recent: 15,
    days: typeof range === 'number' ? range : 90,
    window: range === 'all' ? null : range,
  });

  // Derived metrics
  const metrics = useMemo(() => {
    if (!summary) return null;
    const t = summary.today || {};
    const w = summary.last7d || {};
    const m = summary.last30d || {};
    const all = summary.allTime || {};
    return {
      todayCount: t.tradesCount || 0,
      todayVolume: t.volumeUsd || 0,
      todayFees: t.feesUsd || 0,
      week: w.tradesCount || 0,
      weekVolume: w.volumeUsd || 0,
      month: m.tradesCount || 0,
      monthVolume: m.volumeUsd || 0,
      allCount: all.tradesCount || 0,
      allVolume: all.volumeUsd || 0,
    };
  }, [summary]);

  const sourceBreakdown = useMemo(() => {
    if (!summary?.bySource) return [];
    return summary.bySource.map(s => ({
      ...s,
      label: sourceStyle(s.key).label,
      color: sourceStyle(s.key).hex,
    }));
  }, [summary]);

  const wizardSkipped = onboarding.status?.state?.skipped && !onboarding.status?.state?.completedAt;
  // Effective completed-step count includes BOTH:
  //   - explicit completions from the user clicking through the wizard
  //   - implicit completions detected from platform state (exchange connected,
  //     bot already deployed)
  // Without the implicit count, a user who connected Coinbase + deployed a
  // grid bot via the regular pages would see "0/5 steps completed" and keep
  // getting nagged forever. The backend exposes alreadyConnectedExchange +
  // alreadyHasBot booleans for this exact merge.
  const wizardCompletedSteps = (() => {
    const explicit = new Set(onboarding.status?.state?.completedSteps || []);
    if (onboarding.status?.alreadyConnectedExchange) explicit.add('connect_exchange');
    if (onboarding.status?.alreadyHasBot) explicit.add('create_first_bot');
    return explicit.size;
  })();
  const wizardTotalSteps = onboarding.status?.steps?.length || 5;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" className="text-accent-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
        Could not load activity data: {error}
        <button onClick={refresh} className="ml-3 text-red-300 underline">Retry</button>
      </div>
    );
  }

  const hasAnyActivity = (summary?.counts?.total || 0) > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Wizard modal */}
      {showWizardManually && (
        <OnboardingWizard
          onClose={() => { setShowWizardManually(false); onboarding.refresh(); }}
          onComplete={() => { setShowWizardManually(false); onboarding.refresh(); }}
        />
      )}

      {/* Resume Onboarding banner — only show when there's actually progress
          left to make. If the user already did everything implicitly (exchange
          + bot), the banner disappears even if the wizard was technically
          "skipped". Prevents the perpetual nag we saw on 2026-06-05. */}
      {wizardSkipped && wizardCompletedSteps < wizardTotalSteps && (
        <div className="bg-gradient-to-r from-accent-primary/5 via-violet-500/5 to-cyan-500/5 border border-accent-primary/20 rounded-2xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-primary/15 border border-accent-primary/30 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <p className="font-semibold text-white">Finish setting up haythix</p>
              <p className="text-xs text-gray-400">{wizardCompletedSteps}/{wizardTotalSteps} steps completed — ~3 min to finish</p>
            </div>
          </div>
          <button onClick={() => setShowWizardManually(true)} className="btn-primary text-sm">
            Resume <ArrowRight className="w-3 h-3 inline ml-1" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Portfolio Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">
            Real activity across {Object.keys(summary?.counts?.bySource || {}).length || 0} sources — all your trades, in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700">
            {RANGE_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setRange(opt.id)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all ${range === opt.id ? 'bg-amber-400 text-dark-900' : 'text-gray-400 hover:text-white'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={refresh} className="text-xs text-gray-500 hover:text-white px-3 py-1.5 rounded-lg bg-dark-800 border border-dark-700">
            Refresh
          </button>
        </div>
      </div>

      {/* No activity → onboarding empty state */}
      {!hasAnyActivity && (
        <EmptyState
          icon={Activity}
          title="No trading activity yet"
          description="Once you place your first trade — on CEX, DEX, Smart Trade, or Algo Orders — it will appear here automatically. Every metric below is computed from real fills."
          action={
            <div className="flex items-center gap-2 mt-3 flex-wrap justify-center">
              <Link to="/cex-trading" className="px-4 py-2 rounded-xl bg-amber-500 text-dark-900 text-sm font-bold hover:bg-amber-400">Place CEX Trade</Link>
              <Link to="/swap" className="px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-bold hover:bg-blue-400">DEX Swap</Link>
              <Link to="/smart-trade" className="px-4 py-2 rounded-xl bg-emerald-500 text-dark-900 text-sm font-bold hover:bg-emerald-400">Smart Trade</Link>
              <Link to="/algo-orders" className="px-4 py-2 rounded-xl bg-violet-500 text-white text-sm font-bold hover:bg-violet-400">Algo Orders</Link>
            </div>
          }
        />
      )}

      {hasAnyActivity && (
        <>
          {/* Hero metrics — today */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <HeroMetric
              label="Trades Today"
              value={metrics.todayCount.toLocaleString()}
              subValue={`${formatUsd(metrics.todayVolume)} volume`}
              accent="amber" icon={Activity}
              info="Filled trades across every surface in the current UTC day. Pending limit orders and cancelled orders do not count."
            />
            <HeroMetric
              label="Volume Today"
              value={formatUsd(metrics.todayVolume)}
              subValue={metrics.todayFees > 0 ? `${formatUsd(metrics.todayFees)} fees` : 'No fees recorded'}
              accent="cyan" icon={CurrencyIcon}
              info="Sum of all trade notional values in USD for the current UTC day. A 100 USDC trade counts as $100 regardless of surface (displayed in your selected currency)."
            />
            <HeroMetric
              label="Last 7 Days"
              value={metrics.week.toLocaleString() + ' trades'}
              subValue={formatUsd(metrics.weekVolume) + ' volume'}
              accent="emerald" icon={BarChart3}
              info="Rolling 7-day window (now minus 7 days), not a calendar week. Same UTC boundary as Today."
            />
            <HeroMetric
              label="Last 30 Days"
              value={metrics.month.toLocaleString() + ' trades'}
              subValue={formatUsd(metrics.monthVolume) + ' volume'}
              accent="violet" icon={TrendingUp}
              info="Rolling 30-day window. The time-range selector above does NOT change this number — it's fixed at 30 days."
            />
          </div>

          {/* Time-series chart — title reflects active range */}
          <SectionCard title={range === 'all' ? 'All-Time Activity' : `${range}-Day Activity`} icon={BarChart3}>
            {summary?.timeSeries?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={summary.timeSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor="#f59e0b" stopOpacity={0.6}/>
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={d => format(new Date(d), 'MMM d')}
                    stroke="#6b7280"
                    fontSize={10}
                    tickLine={false} axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280" fontSize={10}
                    tickLine={false} axisLine={false}
                    tickFormatter={formatUsdCompact}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(v, n) => n === 'volumeUsd' ? [`$${Number(v).toLocaleString()}`, 'Volume'] : [v, 'Trades']}
                    labelFormatter={d => format(new Date(d), 'MMM d, yyyy')}
                  />
                  <Area type="monotone" dataKey="volumeUsd" stroke="#f59e0b" strokeWidth={2} fill="url(#volGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-gray-500 py-8 text-center">No activity in the last 30 days.</p>
            )}
          </SectionCard>

          {/* Two-column: source breakdown + top pairs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Activity by Source" icon={Layers}>
              {sourceBreakdown.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={sourceBreakdown}
                        dataKey="tradesCount"
                        nameKey="label"
                        cx="50%" cy="50%" outerRadius={70} innerRadius={45}
                        paddingAngle={2}
                      >
                        {sourceBreakdown.map((s, i) => (
                          <Cell key={i} fill={s.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(v, n) => [`${v} trades`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {sourceBreakdown.map(s => (
                      <div key={s.key} className="flex items-center gap-2 text-[11px]">
                        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                        <span className="text-gray-400">{s.label}</span>
                        <span className="text-white ml-auto font-mono">{s.tradesCount}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-xs text-gray-500 py-8 text-center">No source activity yet.</p>}
            </SectionCard>

            <SectionCard title="Top Trading Pairs" icon={Target}>
              {summary?.bySymbol?.length > 0 ? (
                <div className="space-y-2">
                  {summary.bySymbol.slice(0, 6).map(p => (
                    <div key={p.key} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2">
                      <span className="text-white text-xs font-mono">{p.key}</span>
                      <div className="text-right">
                        <p className="text-white text-xs font-mono">{formatUsd(p.volumeUsd)}</p>
                        <p className="text-gray-500 text-[10px]">{p.tradesCount} trade{p.tradesCount === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-500 py-8 text-center">No pair activity yet.</p>}
            </SectionCard>
          </div>

          {/* By-exchange / by-chain */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="By Exchange (CEX)" icon={Globe}>
              {summary?.byExchange?.length > 0 ? (
                <div className="space-y-2">
                  {summary.byExchange.map(e => (
                    <div key={e.key} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2">
                      <span className="text-white text-xs capitalize">{e.key}</span>
                      <div className="text-right">
                        <p className="text-white text-xs font-mono">{formatUsd(e.volumeUsd)}</p>
                        <p className="text-gray-500 text-[10px]">{e.tradesCount} trade{e.tradesCount === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-500 py-8 text-center">No CEX trades yet.</p>}
            </SectionCard>

            <SectionCard title="By Chain (DEX)" icon={Wallet}>
              {summary?.byChain?.length > 0 ? (
                <div className="space-y-2">
                  {summary.byChain.map(c => (
                    <div key={c.key} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2">
                      <span className="text-white text-xs capitalize">{c.key}</span>
                      <div className="text-right">
                        <p className="text-white text-xs font-mono">{c.tradesCount} swap{c.tradesCount === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-500 py-8 text-center">No DEX activity yet.</p>}
            </SectionCard>
          </div>

          {/* Recent Activity */}
          <SectionCard title="Recent Activity" icon={Clock}
            action={<Link to="/analytics" className="text-xs text-amber-400 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>}>
            {summary?.recentTrades?.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {summary.recentTrades.map((t, i) => {
                  const style = sourceStyle(t.source);
                  const sideColor = t.side === 'buy' || t.side === 'long' ? 'text-emerald-400' :
                                    t.side === 'sell' || t.side === 'short' ? 'text-red-400' : 'text-gray-400';
                  return (
                    <div key={`${t.id}-${i}`} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2.5 border border-dark-600/30">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${style.bg} ${style.text}`}>{style.label}</span>
                        {t.side && <span className={`text-[10px] font-bold uppercase ${sideColor}`}>{t.side}</span>}
                        <span className="text-white text-xs font-mono truncate">{t.symbol || '—'}</span>
                        {t.exchange && <span className="text-gray-500 text-[10px] capitalize">{t.exchange}</span>}
                        {t.chain && <span className="text-gray-500 text-[10px] capitalize">{t.chain}</span>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {t.amountQuote > 0 ? (
                          <p className="text-white text-xs font-mono">{formatUsd(t.amountQuote)}</p>
                        ) : t.fromAmount ? (
                          <p className="text-white text-xs font-mono">{t.fromAmount} {t.symbol?.split('→')[0] || ''}</p>
                        ) : (
                          <p className="text-gray-500 text-xs">—</p>
                        )}
                        <p className="text-gray-600 text-[10px]">
                          {t.ts ? formatDistanceToNow(new Date(t.ts), { addSuffix: true }) : '—'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-xs text-gray-500 py-8 text-center">No recent activity.</p>}
          </SectionCard>
        </>
      )}
    </div>
  );
}
